'use client';

import React from 'react';
import { getBuildingAutocomplete, getSearchNycAutoCompleteApi } from '@repo/api';
import type { AutocompleteSuggestion, NycAutocompleteSuggestion } from '@repo/api';
import { deriveBoroughFromAddress } from '@repo/utils';
import { useStore } from '@/providers/StoreProvider';

export interface UseBuildingAutocompleteOptions {
  minLength?: number; // default: >3
  debounceMs?: number; // default: 300
  useNycApi?: boolean; // default: false - use NYC-specific API instead of general autocomplete
  enabled?: boolean;
}

export function useBuildingAutocomplete(
  externalQuery?: string,
  options: UseBuildingAutocompleteOptions = {},
) {
  const { minLength = 4, debounceMs = 300, useNycApi = false, enabled = true } = options;
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);

  const sessionIdRef = React.useRef<string>(
    crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
  );
  const abortRef = React.useRef<AbortController | null>(null);
  const cacheRef = React.useRef<Map<string, AutocompleteSuggestion[]>>(new Map());

  const [internalQuery, setInternalQuery] = React.useState('');
  const query = externalQuery ?? internalQuery;

  const [suggestions, setSuggestions] = React.useState<AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const normalized = (query || '').trim()?.toLowerCase();

  const clearSuggestions = React.useCallback(() => {
    setSuggestions([]);
  }, []);

  React.useEffect(() => {
    if (!enabled) {
      abortRef.current?.abort();
      setIsLoading(false);
      setError(null);
      setSuggestions([]);
      return;
    }

    if (normalized.length <= minLength - 1) {
      // below threshold: clear and abort
      abortRef.current?.abort();
      setIsLoading(false);
      setError(null);
      setSuggestions([]);
      return;
    }

    const cached = cacheRef.current.get(normalized);
    if (cached) {
      setSuggestions(cached);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const handle = setTimeout(async () => {
      try {
        let suggestions: AutocompleteSuggestion[];

        if (useNycApi) {
          const nycResults = await getSearchNycAutoCompleteApi(query, sessionIdRef.current, {
            signal: controller.signal,
            credentials: isLoggedIn ? 'include' : 'omit',
          });
          suggestions = nycResults || [];
        } else {
          suggestions = await getBuildingAutocomplete(
            { address: query, token: sessionIdRef.current },
            { signal: controller.signal, credentials: isLoggedIn ? 'include' : 'omit' },
          );
        }

        const enriched = suggestions.map(s => ({
          ...s,
          borough: s.borough ?? deriveBoroughFromAddress(s.address),
        }));

        cacheRef.current.set(normalized, enriched);
        setSuggestions(enriched);
        setIsLoading(false);
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        setIsLoading(false);
        setError(e as Error);
      }
    }, debounceMs);

    return () => {
      clearTimeout(handle);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, normalized, debounceMs, minLength, query, useNycApi, isLoggedIn]);

  return {
    query,
    setQuery: setInternalQuery,
    suggestions,
    isLoading,
    error,
    clearSuggestions,
    sessionId: sessionIdRef.current,
  };
}

export default useBuildingAutocomplete;
